import React, { useEffect, useState } from 'react';
import { AtSign, Copy, Loader2, Plus, RotateCcw, Trash2, Eye, EyeOff, Check, Activity, KeyRound, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

// iter175cp — ORB Emails admin page. Lists external-app API keys that
// can send verification / password-reset emails through the
// verify@collectiveorb.com mailbox, and provides UI for the four
// admin actions: create, rotate, revoke, view send history. The
// plaintext API key is shown ONCE — right after creation or rotation
// — and never persisted anywhere retrievable. If the user misses
// their chance to copy it they must rotate again.

const KeyModal = ({ open, onClose, title, apiKey, warning }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      toast.success('API key copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed — highlight and copy manually');
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="orb-emails-key-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#586D52]" />
            {title}
          </DialogTitle>
          <DialogDescription>
            Copy this key now — it will <b>never</b> be shown again. Store it in
            ORB's <code>UPLIFT_API_KEY</code> environment variable.
          </DialogDescription>
        </DialogHeader>
        {warning && (
          <Alert className="border-amber-300/50 bg-amber-50 dark:bg-amber-950/20">
            <AlertTriangle className="w-4 h-4 text-amber-700" />
            <AlertTitle className="text-amber-900 dark:text-amber-100 text-sm">Heads up</AlertTitle>
            <AlertDescription className="text-xs text-amber-800 dark:text-amber-200">{warning}</AlertDescription>
          </Alert>
        )}
        <div className="relative">
          <code className="block w-full font-mono text-[11px] bg-[#f5f4ee] dark:bg-[#1f1f1f] border border-[#e7e5dd] dark:border-[#333] rounded-lg p-3 pr-12 break-all" data-testid="orb-emails-key-value">
            {apiKey}
          </code>
          <button
            onClick={copy}
            className="absolute top-2 right-2 p-1.5 rounded-md hover:bg-white dark:hover:bg-[#2a2a2a]"
            data-testid="orb-emails-copy-key"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
          </button>
        </div>
        <DialogFooter>
          <Button onClick={onClose} variant="secondary" className="w-full" data-testid="orb-emails-close-key-modal">
            I've saved the key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CreateModal = ({ open, onClose, onCreated, api }) => {
  const [displayName, setDisplayName] = useState('');
  const [hourlyLimit, setHourlyLimit] = useState(100);
  const [bypassLimits, setBypassLimits] = useState(true);
  const [busy, setBusy] = useState(false);
  const reset = () => { setDisplayName(''); setHourlyLimit(100); setBypassLimits(true); };

  // Derive a URL/DB-safe slug from the display name.
  //   "ORB"       -> "orb"
  //   "ORB Corp"  -> "orb-corp"
  //   "Foo & Bar" -> "foo-bar"
  const slugify = (s) => (s || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

  const submit = async () => {
    const display = displayName.trim();
    if (!display) {
      toast.error('Name is required');
      return;
    }
    const slug = slugify(display);
    if (!slug) {
      toast.error('Name must include letters or numbers');
      return;
    }
    setBusy(true);
    try {
      // `name` is the internal slug; server uniqueness is enforced on this.
      // If the slug is taken we append a short suffix so admins never have
      // to think about the slug field.
      let attempt = slug;
      let data = null;
      for (let i = 0; i < 5 && !data; i++) {
        try {
          const res = await api.post('/external/emails/admin/apps', {
            name: attempt,
            display_name: display,
            hourly_limit: hourlyLimit,
            bypass_rate_limits: bypassLimits,
          });
          data = res.data;
        } catch (err) {
          if (err?.response?.status === 409) {
            attempt = `${slug}-${Math.random().toString(36).slice(2, 5)}`;
            continue;
          }
          throw err;
        }
      }
      if (!data) {
        toast.error('Could not find a unique slug — please rename slightly.');
        return;
      }
      onCreated(data);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create app');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); reset(); } }}>
      <DialogContent className="max-w-md" data-testid="orb-emails-create-modal">
        <DialogHeader>
          <DialogTitle>New API Key</DialogTitle>
          <DialogDescription>Provision a new external app that can send verify + reset emails.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Name (shown in email body)</Label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="ORB"
              data-testid="orb-emails-create-display"
            />
            {displayName.trim() && (
              <p className="text-[10px] text-muted-foreground mt-1">
                Internal slug: <span className="font-mono">{slugify(displayName) || '—'}</span>
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs">Hourly send limit (per-app)</Label>
            <Input type="number" value={hourlyLimit} onChange={(e) => setHourlyLimit(parseInt(e.target.value || '0', 10))} data-testid="orb-emails-create-limit" disabled={bypassLimits} />
          </div>
          <div className="flex items-center justify-between rounded-md border border-[#e7e5dd] dark:border-[#333] p-2.5">
            <div>
              <p className="text-xs font-semibold">Bypass rate limits</p>
              <p className="text-[11px] text-muted-foreground">Skip both per-app and per-recipient caps</p>
            </div>
            <Switch checked={bypassLimits} onCheckedChange={setBypassLimits} data-testid="orb-emails-create-bypass" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => { onClose(); reset(); }} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="orb-emails-create-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AppRow = ({ app, onRotate, onRevoke, busyId }) => {
  const isActive = app.is_active !== false;
  const isBusy = busyId === app.id;
  return (
    <div className={`border rounded-xl p-3 mb-2 ${isActive ? 'border-[#e7e5dd] dark:border-[#333] bg-white dark:bg-[#1a1a1a]' : 'border-red-300/40 bg-red-50/30 dark:bg-red-950/10 opacity-70'}`} data-testid={`orb-emails-app-row-${app.name}`}>
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-sm truncate">{app.display_name}</p>
            <span className="text-[10px] font-mono text-muted-foreground">{app.name}</span>
            {!isActive && <span className="text-[10px] bg-red-500/10 text-red-700 dark:text-red-300 rounded-full px-1.5 py-0.5">revoked</span>}
            {app.bypass_rate_limits && <span className="text-[10px] bg-[#586D52]/10 text-[#586D52] rounded-full px-1.5 py-0.5">unlimited</span>}
            {app.previous_key_active && (
              <span className="text-[10px] bg-amber-500/10 text-amber-700 dark:text-amber-300 rounded-full px-1.5 py-0.5" title={`Old key still valid until ${app.previous_key_expires_at}`}>
                old key active
              </span>
            )}
          </div>
          <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">
            Key prefix: {app.key_prefix}··· · Created {app.created_at?.slice(0, 10)}
            {app.rotated_at && ` · Rotated ${app.rotated_at.slice(0, 10)}`}
          </p>
        </div>
        {isActive && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRotate(app)}
              disabled={isBusy}
              className="text-[11px] h-8 px-2"
              data-testid={`orb-emails-rotate-${app.name}`}
            >
              {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
              <span className="ml-1">Rotate</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRevoke(app)}
              disabled={isBusy}
              className="text-[11px] h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
              data-testid={`orb-emails-revoke-${app.name}`}
            >
              <Trash2 className="w-3 h-3" />
              <span className="ml-1">Revoke</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const SendsPanel = ({ appId, api }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!expanded) return;
    setLoading(true);
    (async () => {
      try {
        const params = appId ? `?app_id=${encodeURIComponent(appId)}&limit=25` : '?limit=25';
        const { data } = await api.get(`/external/emails/admin/sends${params}`);
        setRows(data || []);
      } catch (e) {
        toast.error('Failed to load send history');
      } finally {
        setLoading(false);
      }
    })();
  }, [expanded, appId]);
  return (
    <div className="mt-4 border border-[#e7e5dd] dark:border-[#333] rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 bg-[#f5f4ee] dark:bg-[#1f1f1f] hover:bg-[#eeecdf] dark:hover:bg-[#252525]"
        data-testid="orb-emails-toggle-sends"
      >
        <span className="flex items-center gap-1.5 text-xs font-semibold">
          <Activity className="w-3.5 h-3.5" /> Recent sends
        </span>
        {expanded ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      </button>
      {expanded && (
        <div className="p-2 space-y-1 max-h-64 overflow-y-auto">
          {loading && <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin" /></div>}
          {!loading && rows.length === 0 && <p className="text-[11px] text-muted-foreground text-center py-3">No sends yet</p>}
          {!loading && rows.map((r) => (
            <div key={r.id} className="text-[11px] flex items-center justify-between gap-2 border-b border-[#e7e5dd]/30 dark:border-[#333]/30 pb-1">
              <div className="min-w-0 flex-1">
                <p className="truncate">
                  <span className="font-mono text-[10px] bg-gray-100 dark:bg-[#1f1f1f] rounded px-1">{r.kind === 'verify_email' ? 'VRFY' : 'RSET'}</span>
                  <span className="ml-1 font-semibold">{r.app_name}</span>
                  <span className="ml-1 text-muted-foreground">→ {r.to_email}</span>
                </p>
              </div>
              <div className="flex-shrink-0 text-right">
                <span className={`text-[10px] font-semibold ${r.outbox_status === 'sent' ? 'text-emerald-600' : 'text-red-600'}`}>{r.outbox_status}</span>
                <p className="text-[9px] text-muted-foreground">{r.created_at?.slice(0, 16).replace('T', ' ')}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminOrbEmailsPage = () => {
  const { api } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [keyModal, setKeyModal] = useState(null); // { title, apiKey, warning }
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/external/emails/admin/apps');
      setApps(data || []);
    } catch (e) {
      toast.error('Failed to load ORB email apps');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleCreated = (data) => {
    setCreateOpen(false);
    setKeyModal({
      title: `Key for "${data.display_name}"`,
      apiKey: data.api_key,
    });
    load();
  };

  const handleRotate = async (app) => {
    if (!window.confirm(`Rotate the API key for "${app.display_name}"?\n\nThe new key becomes active immediately.\nThe OLD key stays valid for 3 more hours so you have time to redeploy the receiving app without downtime.`)) return;
    setBusyId(app.id);
    try {
      const { data } = await api.post(`/external/emails/admin/apps/${app.id}/rotate`);
      const graceH = data.grace_period_hours || 3;
      setKeyModal({
        title: `New key for "${app.display_name}"`,
        apiKey: data.api_key,
        warning: `Both keys work right now. The old key stops working in ${graceH} hours (${data.previous_key_expires_at?.slice(0, 16).replace('T', ' ')} UTC). Update the receiving app before then.`,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rotation failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleRevoke = async (app) => {
    if (!window.confirm(`Revoke the API key for "${app.display_name}"?\n\nThe key will stop working immediately and CANNOT be reinstated. To restore access you'd have to create a new app.`)) return;
    setBusyId(app.id);
    try {
      await api.post(`/external/emails/admin/apps/${app.id}/revoke`);
      toast.success(`"${app.display_name}" revoked`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revoke failed');
    } finally {
      setBusyId(null);
    }
  };

  const activeApps = apps.filter((a) => a.is_active !== false);
  const revokedApps = apps.filter((a) => a.is_active === false);

  return (
    <div className="p-4 max-w-2xl mx-auto pb-24" data-testid="orb-emails-page">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-lg font-bold flex items-center gap-2">
              <AtSign className="w-5 h-5 text-[#586D52]" /> ORB Emails
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              API keys that let external apps send verify + password-reset emails through <span className="font-mono">verify@collectiveorb.com</span>.
            </p>
          </div>
          <Button onClick={() => setCreateOpen(true)} size="sm" className="flex-shrink-0" data-testid="orb-emails-new-key-btn">
            <Plus className="w-4 h-4 mr-1" /> New Key
          </Button>
        </div>

        {loading && (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
        )}

        {!loading && apps.length === 0 && (
          <div className="text-center py-10 border border-dashed rounded-xl">
            <AtSign className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">No API keys yet</p>
            <p className="text-[11px] text-muted-foreground/70 mt-1">Click "New Key" to provision one for ORB or another external app.</p>
          </div>
        )}

        {!loading && activeApps.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Active</p>
            {activeApps.map((app) => (
              <AppRow key={app.id} app={app} onRotate={handleRotate} onRevoke={handleRevoke} busyId={busyId} />
            ))}
          </div>
        )}

        {!loading && revokedApps.length > 0 && (
          <div className="mt-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Revoked</p>
            {revokedApps.map((app) => (
              <AppRow key={app.id} app={app} onRotate={handleRotate} onRevoke={handleRevoke} busyId={busyId} />
            ))}
          </div>
        )}

        {!loading && apps.length > 0 && (
          <SendsPanel appId={null} api={api} />
        )}

      <CreateModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleCreated} api={api} />
      {keyModal && (
        <KeyModal
          open={!!keyModal}
          onClose={() => setKeyModal(null)}
          title={keyModal.title}
          apiKey={keyModal.apiKey}
          warning={keyModal.warning}
        />
      )}
    </div>
  );
};

export default AdminOrbEmailsPage;
